import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group, TextInput, Tooltip } from '@mantine/core';
import { useIntl } from 'react-intl';

import { addressFormMessages } from './address-form-messages';
import { CountryPicker } from './country-picker';
import { StatePicker } from './state-picker';
import { CustomerAutocomplete } from '../components/common/customer-autocomplete';

import type { AddressFieldSchema } from './types';
import type { Customer } from '../types/customer';
import type { Country, State } from '../types/reference-data';

const AUTOCOMPLETE_TOKEN_MAP: Record<string, string> = {
  name: 'name',
  company: 'organization',
  line1: 'address-line1',
  line2: 'address-line2',
  line3: 'address-line3',
  city: 'address-level2',
  state: 'address-level1',
  postalCode: 'postal-code',
  countryCode: 'country-name',
  phone: 'tel',
  email: 'email',
};

const getAutoComplete = (fieldName: string, section?: string): string | undefined => {
  const token = AUTOCOMPLETE_TOKEN_MAP[fieldName];
  if (!token) return undefined;
  return section ? `${section} ${token}` : token;
};

type FieldRendererProps = {
  field: AddressFieldSchema;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onFieldBlur?: (fieldName: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  countryCode?: string;
  autocompleteSection?: string;
  // Reference data injected by AddressForm so pickers stay stateless.
  countries: Country[];
  isLoadingCountries: boolean;
  sellerHomeCountry: string;
  states: State[];
  isLoadingStates: boolean;
  labelTooltip?: string;
  /** Called with the full record when a customer suggestion is picked in the `name` field. */
  onCustomerSelect?: (customer: Customer) => void;
  customerCountryCodes?: readonly string[];
};

const getMessageFromKey = (messageKey: string, intl: ReturnType<typeof useIntl>): string => {
  const parts = messageKey.split('.');
  const key = parts[parts.length - 1];
  const messageId = toCamelCase(key ?? '');
  if (messageId in addressFormMessages) {
    return intl.formatMessage(addressFormMessages[messageId as keyof typeof addressFormMessages]);
  }
  return key ?? '';
};

const toCamelCase = (str: string): string => str.replace(/[-_]([a-z])/g, (_, letter: string) => letter.toUpperCase());

export const FieldRenderer = ({
  field,
  value,
  onChange,
  onBlur,
  onFieldBlur,
  hasError,
  errorMessage,
  disabled,
  countryCode,
  autocompleteSection,
  countries,
  isLoadingCountries,
  sellerHomeCountry,
  states,
  isLoadingStates,
  labelTooltip,
  onCustomerSelect,
  customerCountryCodes,
}: FieldRendererProps) => {
  const intl = useIntl();

  const labelText = getMessageFromKey(field.labelMessageKey, intl);
  const placeholder = getMessageFromKey(field.placeholderMessageKey, intl);
  const label = field.labelMessageKey && field.labelMessageKey.trim() !== '' ? labelText : undefined;

  const fieldLabel = labelTooltip ? (
    <Group align='center' gap='xs'>
      {label}
      <Tooltip label={labelTooltip} multiline maw={180} withArrow>
        <FontAwesomeIcon icon={faCircleInfo} height={16} width={16} />
      </Tooltip>
    </Group>
  ) : (
    label
  );
  const isRequired = field.validation.some((rule) => rule.type === 'required' && rule.value === true);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.currentTarget.value);
  };

  const handleBlur = () => {
    onBlur?.(value);
    onFieldBlur?.(field.name);
  };

  const autoComplete = getAutoComplete(field.name, autocompleteSection);

  const textInputProps = {
    label: fieldLabel,
    placeholder,
    value,
    onChange: handleChange,
    onBlur: handleBlur,
    required: isRequired,
    error: hasError ? errorMessage : undefined,
    disabled,
    autoComplete,
    'data-testid': `address-field-${field.name}`,
  };

  switch (field.type) {
    case 'email':
      return <TextInput {...textInputProps} type='email' />;
    case 'phone':
      return <TextInput {...textInputProps} type='tel' />;
    case 'select':
      if (field.name === 'countryCode') {
        return (
          <CountryPicker
            autocompleteSection={autocompleteSection}
            countries={countries}
            data-testid={`address-field-${field.name}`}
            disabled={disabled}
            error={hasError ? errorMessage : undefined}
            isLoadingCountries={isLoadingCountries}
            onChange={onChange}
            required={isRequired}
            sellerHomeCountry={sellerHomeCountry}
            value={value}
          />
        );
      }
      if (field.name === 'state' && countryCode) {
        return (
          <StatePicker
            autocompleteSection={autocompleteSection}
            countryCode={countryCode}
            data-testid={`address-field-${field.name}`}
            disabled={disabled}
            error={hasError ? errorMessage : undefined}
            isLoadingStates={isLoadingStates}
            label={label ?? ''}
            onChange={onChange}
            required={isRequired}
            states={states}
            value={value}
          />
        );
      }
      return <TextInput {...textInputProps} />;
    default:
      if (field.name === 'name') {
        return (
          <CustomerAutocomplete
            countryCodes={customerCountryCodes}
            data-testid={`address-field-${field.name}`}
            disabled={disabled}
            error={hasError ? errorMessage : undefined}
            isRequired={isRequired}
            label={label ?? ''}
            onChange={onChange}
            onCustomerSelect={onCustomerSelect}
            placeholder={placeholder}
            value={value}
          />
        );
      }
      return <TextInput {...textInputProps} />;
  }
};
